/**
 * User Management Error Handling Utilities
 * Based on Streamlit implementation: All Spanish error messages throughout secure_auth.py
 * Requirements Reference: TAR3 (API Client Integration), NFR6 (User Experience Consistency)
 * Design Reference: Section 4.2 (Error Response Format)
 * 
 * Provides centralized error handling for user management operations with Spanish messages
 * matching Streamlit exactly. Maps technical errors to user-friendly Spanish messages.
 * 
 * Key Features:
 * - Exact Spanish error message matching from Streamlit
 * - Domain-specific error categorization
 * - Consistent error display patterns
 * - Actionable error recovery suggestions
 */

// Phase 97b: Dead constants removed (D8). 13 unused constants deleted — see bugs.md BUG-002.
// Remaining constants are actively referenced by parseUserManagementError() mappings,
// UserManagementContext.tsx, or sessionValidationService.ts.
export const USER_MANAGEMENT_ERRORS = {
  // Authentication and session errors
  ACCESS_DENIED: '🚫 Acceso denegado',
  ADMIN_PRIVILEGES_REQUIRED: 'Se requieren privilegios de administrador',

  // User creation errors
  USER_CREATION_FAILED: 'Error al crear usuario',
  REQUIRED_FIELDS_MISSING: '⚠️ Se requieren nombre de usuario y correo electrónico',
  USER_ALREADY_EXISTS: 'El usuario ya existe en el sistema',
  INVALID_EMAIL_FORMAT: 'Formato de correo electrónico inválido',

  // User role management errors
  ROLE_UPDATE_FAILED: 'Error actualizando rol de usuario',
  INVALID_ROLE: 'Rol inválido',

  // User deletion errors
  USER_DELETION_FAILED: 'Error eliminando usuario',
  USER_NOT_FOUND: 'Usuario no encontrado',

  // Password reset errors
  PASSWORD_RESET_FAILED: 'Error restableciendo contraseña',

  // System and network errors
  NETWORK_ERROR: 'Error de conexión. Por favor, verifica tu conexión a internet',
  SERVER_ERROR: 'Error del servidor. Por favor, intenta más tarde',
  TIMEOUT_ERROR: 'La operación ha tardado demasiado. Por favor, intenta nuevamente',
  DATABASE_ERROR: 'Error en la base de datos. Contacta al administrador del sistema',

  // Loading and general errors
  LOADING_USERS_FAILED: 'Error cargando usuarios',
  UNKNOWN_ERROR: 'Error desconocido. Contacta al soporte técnico',

  // Rate limiting errors
  RATE_LIMIT_EXCEEDED: 'Demasiadas operaciones. Por favor, espera un momento antes de intentar nuevamente',
  TOO_MANY_REQUESTS: 'Demasiadas solicitudes. Intenta nuevamente en unos minutos'
}

// Success messages matching Streamlit
export const USER_MANAGEMENT_SUCCESS = {
  USER_CREATED: 'Usuario creado exitosamente',
  USER_UPDATED: 'Usuario actualizado exitosamente',
  USER_DELETED: 'Usuario eliminado exitosamente',
  ROLE_UPDATED: 'Rol actualizado exitosamente',
  PASSWORD_RESET: 'Contraseña restablecida exitosamente',
  OPERATION_COMPLETED: 'Operación completada exitosamente'
}

// Loading messages matching Streamlit spinners
export const USER_MANAGEMENT_LOADING = {
  CREATING_USER: 'Creando usuario...',
  UPDATING_ROLE: 'Actualizando rol...',
  DELETING_USER: 'Eliminando usuario...',
  RESETTING_PASSWORD: 'Restableciendo contraseña...',
  LOADING_USERS: 'Cargando usuarios...',
  AUTHENTICATING: 'Autenticando...',
  PROCESSING: 'Procesando operación...'
}

/**
 * Error parsing utility that extracts meaningful Spanish messages from API errors
 * @param {Error} error - The error object from API call
 * @param {string} operation - The operation being performed (for context)
 * @returns {string} - User-friendly Spanish error message
 */
export const parseUserManagementError = (error: any, operation: string = ''): string => {
  // Extract error message from different possible structures
  let errorMessage = ''
  
  if (error.response) {
    // API error response
    errorMessage = error.response.data?.message || 
                   error.response.data?.detail || 
                   error.response.data?.error ||
                   error.message
  } else if (error.message) {
    // Direct error message
    errorMessage = error.message
  } else {
    // Unknown error structure
    errorMessage = String(error)
  }
  
  // Map common error patterns to Spanish messages
  const errorMappings = {
    'access denied': USER_MANAGEMENT_ERRORS.ACCESS_DENIED,
    'unauthorized': USER_MANAGEMENT_ERRORS.ADMIN_PRIVILEGES_REQUIRED,
    'user not found': USER_MANAGEMENT_ERRORS.USER_NOT_FOUND,
    'user already exists': USER_MANAGEMENT_ERRORS.USER_ALREADY_EXISTS,
    'invalid email': USER_MANAGEMENT_ERRORS.INVALID_EMAIL_FORMAT,
    'required fields': USER_MANAGEMENT_ERRORS.REQUIRED_FIELDS_MISSING,
    'network error': USER_MANAGEMENT_ERRORS.NETWORK_ERROR,
    'timeout': USER_MANAGEMENT_ERRORS.TIMEOUT_ERROR,
    'rate limit': USER_MANAGEMENT_ERRORS.RATE_LIMIT_EXCEEDED,
    'too many requests': USER_MANAGEMENT_ERRORS.TOO_MANY_REQUESTS,
    'database error': USER_MANAGEMENT_ERRORS.DATABASE_ERROR,
    'server error': USER_MANAGEMENT_ERRORS.SERVER_ERROR
  }
  
  // Check for specific error patterns
  const lowerErrorMessage = errorMessage.toLowerCase()
  for (const [pattern, spanishMessage] of Object.entries(errorMappings)) {
    if (lowerErrorMessage.includes(pattern)) {
      return spanishMessage
    }
  }

  // Phase 97b BUG-002: Prefer backend's actual message when available.
  // The backend returns helpful Spanish messages (e.g., "Los roles deben ser uno de: ...").
  // Only fall to generic operation messages for non-API errors (network, client-side).
  if (error.response && errorMessage) {
    return errorMessage
  }

  // Operation-specific fallback for non-API errors
  switch (operation.toLowerCase()) {
    case 'create':
    case 'creating':
      return USER_MANAGEMENT_ERRORS.USER_CREATION_FAILED
    case 'update':
    case 'updating':
    case 'role':
      return USER_MANAGEMENT_ERRORS.ROLE_UPDATE_FAILED
    case 'delete':
    case 'deleting':
      return USER_MANAGEMENT_ERRORS.USER_DELETION_FAILED
    case 'reset':
    case 'password':
      return USER_MANAGEMENT_ERRORS.PASSWORD_RESET_FAILED
    case 'load':
    case 'loading':
      return USER_MANAGEMENT_ERRORS.LOADING_USERS_FAILED
    default:
      return errorMessage || USER_MANAGEMENT_ERRORS.UNKNOWN_ERROR
  }
}

/**
 * Get appropriate loading message for operation
 * @param {string} operation - The operation being performed
 * @returns {string} - Spanish loading message matching Streamlit spinners
 */
export const getLoadingMessage = (operation: string): string => {
  const loadingMappings = {
    'create': USER_MANAGEMENT_LOADING.CREATING_USER,
    'creating': USER_MANAGEMENT_LOADING.CREATING_USER,
    'update': USER_MANAGEMENT_LOADING.UPDATING_ROLE,
    'updating': USER_MANAGEMENT_LOADING.UPDATING_ROLE,
    'role': USER_MANAGEMENT_LOADING.UPDATING_ROLE,
    'delete': USER_MANAGEMENT_LOADING.DELETING_USER,
    'deleting': USER_MANAGEMENT_LOADING.DELETING_USER,
    'reset': USER_MANAGEMENT_LOADING.RESETTING_PASSWORD,
    'password': USER_MANAGEMENT_LOADING.RESETTING_PASSWORD,
    'load': USER_MANAGEMENT_LOADING.LOADING_USERS,
    'loading': USER_MANAGEMENT_LOADING.LOADING_USERS,
    'auth': USER_MANAGEMENT_LOADING.AUTHENTICATING,
    'authenticating': USER_MANAGEMENT_LOADING.AUTHENTICATING
  }
  
  return loadingMappings[operation.toLowerCase()] || USER_MANAGEMENT_LOADING.PROCESSING
}

/**
 * Get appropriate success message for operation
 * @param {string} operation - The completed operation
 * @param {Object} data - Additional data (like username) for personalized messages
 * @returns {string} - Spanish success message
 */
export const getSuccessMessage = (operation: string, data: Record<string, any> = {}): string => {
  switch (operation.toLowerCase()) {
    case 'create':
    case 'created':
      return data.username 
        ? `Usuario ${data.username} creado exitosamente`
        : USER_MANAGEMENT_SUCCESS.USER_CREATED
    case 'update':
    case 'updated':
    case 'role':
      return USER_MANAGEMENT_SUCCESS.ROLE_UPDATED
    case 'delete':
    case 'deleted':
      return USER_MANAGEMENT_SUCCESS.USER_DELETED
    case 'reset':
    case 'password':
      return USER_MANAGEMENT_SUCCESS.PASSWORD_RESET
    default:
      return USER_MANAGEMENT_SUCCESS.OPERATION_COMPLETED
  }
}

/**
 * Enhanced toast error display with consistent styling
 * @param {Error} error - The error to display
 * @param {string} operation - The operation context
 */
export const showUserManagementError = (error: any, operation: string = ''): void => {
  const message = parseUserManagementError(error, operation)
  
  // Use toast with error styling matching Streamlit's st.error()
  import('react-hot-toast').then(({ default: toast }) => {
    toast.error(message, {
      duration: 6000, // Longer duration for error messages
      style: {
        background: '#ffebee', // Light red background like Streamlit
        color: '#c62828', // Dark red text
        border: '1px solid #ef5350',
        fontWeight: '500'
      },
      iconTheme: {
        primary: '#f44336',
        secondary: '#fff'
      }
    })
  })
}

/**
 * Enhanced toast success display with consistent styling
 * @param {string} operation - The completed operation
 * @param {Object} data - Additional data for personalized messages
 */
export const showUserManagementSuccess = (operation: string, data: Record<string, any> = {}): void => {
  const message = getSuccessMessage(operation, data)
  
  // Use toast with success styling matching Streamlit's st.success()
  import('react-hot-toast').then(({ default: toast }) => {
    toast.success(message, {
      duration: 4000,
      style: {
        background: '#e8f5e8', // Light green background like Streamlit
        color: '#2e7d32', // Dark green text
        border: '1px solid #66bb6a',
        fontWeight: '500'
      },
      iconTheme: {
        primary: '#4caf50',
        secondary: '#fff'
      }
    })
  })
}

export default {
  USER_MANAGEMENT_ERRORS,
  USER_MANAGEMENT_SUCCESS, 
  USER_MANAGEMENT_LOADING,
  parseUserManagementError,
  getLoadingMessage,
  getSuccessMessage,
  showUserManagementError,
  showUserManagementSuccess
}